import React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import _ from "lodash";

import { LoadingState, EmptyState } from "./states";
import { TableCellText } from "./TableCellText";
import { formatDurationWithLabel } from "../utils/format";
import styles from "./common.module.scss";

const PIE_COLORS = ["#E63462", "#EC6285", "#F291AA", "#F8C0CF", "#DCDCDC"];

export interface RadialSlice {
  name: string;
  value: number;
  display?: string;
}

export const metricsToRadialSlices = (
  records: any[],
  viewOption: { value?: string; name?: string },
): RadialSlice[] => {
  const valueKey = viewOption.value || "views";
  const nameKey = viewOption.name || "name";
  const isTime = /time/i.test(valueKey);
  return records
    .map((row) => {
      const value = Number(_.get(row, valueKey)) || 0;
      return {
        name: String(_.get(row, nameKey) ?? "-") || "-",
        value,
        display: isTime ? formatDurationWithLabel(value).value : String(value),
      };
    })
    .filter((s) => s.value > 0);
};

interface LeadsRadialPieChartProps {
  data?: RadialSlice[];
  measureLabel?: string;
  height?: number;
  loading?: boolean;
}

export const LeadsRadialPieChart: React.FC<LeadsRadialPieChartProps> = ({
  data = [],
  measureLabel = "Value",
  height = 280,
  loading,
}) => {
  if (loading) return <LoadingState style={{ minHeight: height }} />;
  if (!data || data.length === 0) return <EmptyState style={{ minHeight: height }} />;

  const slices = [...data]
    .sort((a, b) => b.value - a.value)
    .map((d, i) => ({ ...d, color: PIE_COLORS[i % PIE_COLORS.length] }));

  return (
    <div className={styles.radialPie}>
      <div className={styles.radialPieChart}>
        <ResponsiveContainer width="100%" height={height}>
          <PieChart margin={{ top: 24, right: 24, bottom: 24, left: 24 }}>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              innerRadius="68%"
              outerRadius="80%"
              startAngle={90}
              endAngle={-270}
              paddingAngle={slices.length > 1 ? 5 : 0}
              cornerRadius={10}
              stroke="none"
              labelLine={false}
              label={renderSliceLabel}
            >
              {slices.map((entry, index) => (
                <Cell key={`${entry.name}-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={(props) => <RadialTooltip {...props} measureLabel={measureLabel} />} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <ul className={styles.radialPieLegend}>
        {slices.map((entry, index) => (
          <li key={`${entry.name}-${index}`}>
            <span className={styles.radialPieDot} style={{ backgroundColor: entry.color }} />
            <TableCellText className={styles.radialPieKey} text={entry.name} />
          </li>
        ))}
      </ul>
    </div>
  );
};

const RAD = Math.PI / 180;

const renderSliceLabel = (props: any) => {
  const { cx, cy, midAngle, outerRadius, percent } = props;
  if (percent == null || Number.isNaN(percent)) return null;
  const text = `${Math.round(percent * 100)}%`;

  const radius = outerRadius + 16;
  const x = cx + radius * Math.cos(-midAngle * RAD);
  const y = cy + radius * Math.sin(-midAngle * RAD);

  return (
    <text
      x={x}
      y={y}
      fill="#5C5C5C"
      fontSize={13}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {text}
    </text>
  );
};

const RadialTooltip: React.FC<{
  active?: boolean;
  payload?: any[];
  measureLabel: string;
}> = ({ active, payload, measureLabel }) => {
  if (!active || !payload || !payload.length) return null;
  const datum = _.get(payload, "[0].payload", null);
  if (!datum) return null;

  const value = _.get(datum, "display", _.get(datum, "value", ""));

  return (
    <div className={styles.barChartTooltip}>
      <p className="key">{_.get(datum, "name", "")}</p>
      <p className="value">{`${measureLabel} : ${value}`}</p>
    </div>
  );
};
